import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "./sidebarThreadRuntimeIdentity";

interface CanonicalDispositionThread {
  readonly environmentId: string;
  readonly id: string;
  readonly settledAt: string | null;
  readonly settledOverride: "active" | "settled" | null;
  readonly archive?: import("@cmmd-forge/contracts").CmmdThreadArchiveSummary | null | undefined;
}

/**
 * Keep live runtime detail while taking filing state only from CMMD.
 *
 * Matched by runtime-scoped identity, not by raw environment id: CMMD names a
 * cloud thread by the `cmmd-runtime:` locator while a live connection names the
 * same thread by the durable `env_…` key. Comparing the raw ids let every live
 * row fall through unmatched, so a thread the user settled in CMMD came back
 * as Active the moment its runtime connected (FOR-36687).
 */
export function overlayCanonicalThreadDispositions<TThread extends CanonicalDispositionThread>(
  liveThreads: readonly TThread[],
  disconnectedRuntimeIds: ReadonlySet<TThread["environmentId"]>,
  cmmdThreads: readonly CanonicalDispositionThread[],
  // Required, not defaulted: a call site that forgets the registry reproduces
  // the bug silently, so the compiler has to be the one that notices.
  environments: SavedEnvironmentRuntimeIndex,
): readonly TThread[] {
  const keyOf = (thread: CanonicalDispositionThread) =>
    runtimeScopedSidebarKey(thread.environmentId, thread.id, environments);
  const canonicalByKey = new Map(cmmdThreads.map((thread) => [keyOf(thread), thread] as const));
  return liveThreads
    .filter((thread) => !disconnectedRuntimeIds.has(thread.environmentId))
    .map((thread) => {
      const canonical = canonicalByKey.get(keyOf(thread));
      return canonical
        ? Object.assign({}, thread, {
            settledAt: canonical.settledAt,
            settledOverride: canonical.settledOverride,
            archive: canonical.archive ?? null,
          })
        : thread;
    });
}
